import React, { useState, useEffect, useCallback } from 'react';
import { openDB } from 'idb';
import { useTranslation } from 'react-i18next';

const SAMPLE_POEMS = {
  en: "Through CRISPR's dance with DNA's design,\nAnd language models' wisdom vast and deep,\nAI guides healers on their grand design,\nAs ancient ailments fade while patients sleep.\nLike stars that light the darkest human night,\nThese tools of science bring new hope and care,\nWhere data flows and algorithms bright\nSeek cures that mortal eyes could never dare.\nIn labs where genes are edited with grace,\nAnd neural nets predict what lies ahead,\nWe glimpse a future for our human race\nWhere suffering and pain at last lie dead.\nThrough technology's transforming art,\nWe heal both body, mind, and human heart.",
  zh: "基因编辑展新篇，\n智能医疗济世间。\n数据如泉昼夜涌，\n算法似剑破难关。\n科研报国兴百业，\n创新济世利千年。\n但愿众生皆安康，\n春风化雨满人间。"
};

const FREE_POEM_PREFIX = "我要你扮演诗人。用以下的文章题目做首古诗";

const normalizePoemLanguage = (language) => (
  typeof language === 'string' && language.toLowerCase().startsWith('zh') ? 'zh' : 'en'
);

const poemMatchesLanguage = (poemText, language) => {
  if (!poemText) return false;

  const hasChinese = /[\u4e00-\u9fff]/.test(poemText);
  return language === 'zh' ? hasChinese : !hasChinese;
};

const getFallbackTitle = (language) => (
  language === 'zh' ? '默认论文题目' : 'Untitled paper'
);

const buildPoemPrompt = (language) => {
  if (language === 'en') {
    return `${FREE_POEM_PREFIX}。上面这句中文只是系统计费兼容标记，不代表输出语言。Your actual task: write exactly one original poem in English based on the academic paper titles below. Output English only. Do not include any Chinese. Do not explain the poem. Make it elegant, vivid, and research-inspired, with strong imagery and emotional clarity.`;
  }

  return `${FREE_POEM_PREFIX}，你同时也是这些学术文章的科学家。请只用中文写作，只输出一首高质量、意境完整、语言凝练的七言律诗，不要解释，不要英文。`;
};

const getStoredToken = () => {
  const authProvider = localStorage.getItem('auth_provider');

  if (authProvider === 'email') {
    return localStorage.getItem('email_token') || localStorage.getItem('access_token');
  }

  return (
    localStorage.getItem('wechat_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('email_token')
  );
};

const GeneratedPoem = ({ isDarkMode }) => {
  const [poem, setPoem] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { i18n } = useTranslation();
  const poemLanguage = normalizePoemLanguage(i18n.language);

  const getPDFTitles = async (language) => {
    try {
      const db = await openDB('PDFStore', 4);
      const allMetadata = await db.getAll('metadata');
      
      if (!allMetadata || allMetadata.length === 0) {
        return [getFallbackTitle(language)];
      }
      
      return allMetadata.map((metadata) => metadata.name || getFallbackTitle(language));
    } catch (error) {
      console.error('Error getting PDF titles:', error);
      return [getFallbackTitle(language)];
    }
  };

  const generatePoem = useCallback(async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const titles = await getPDFTitles(poemLanguage);
      if (!titles || titles.length === 0) {
        setError('No PDF titles found for poem generation');
        setIsLoading(false);
        return;
      }
      const prompt = buildPoemPrompt(poemLanguage);
      
      const text = titles.join(", ");
      
      const model = 'GLM-4-Flash';
      const token = getStoredToken();
      
      if (!token) {
        setIsAuthenticated(false);
        setPoem(SAMPLE_POEMS[poemLanguage] || SAMPLE_POEMS.en);
        return false;
      }

      const API_URL = 'https://api.labcat.com.cn/moonshot/generate_moonshot';
      
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          text,
          model,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          setIsAuthenticated(false);
          setPoem(SAMPLE_POEMS[poemLanguage] || SAMPLE_POEMS.en);
          setError('');
          return false;
        }
        throw new Error(`API request failed with status ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Failed to get a valid response body from the API');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let fullPoem = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.trim().startsWith('data:') && line.trim() !== 'data: [DONE]') {
            try {
              // Skip token usage data
              if (line.includes('"token_usage"')) continue;
              
              const jsonData = JSON.parse(line.substring(5));
              if (jsonData.error) {
                console.error('Error from server:', jsonData.error);
                throw new Error(jsonData.error);
              }
              
              if (jsonData.choices && jsonData.choices[0]?.delta?.content) {
                const content = jsonData.choices[0].delta.content;
                fullPoem += content;
                setPoem(fullPoem);
                localStorage.setItem('generatedPoem', fullPoem);
                localStorage.setItem('poemLanguage', poemLanguage);
                setIsAuthenticated(true);
              }
            } catch (error) {
              if (error.message !== 'Unexpected end of JSON input') {
                console.error('Error parsing JSON:', error);
              }
            }
          }
        }
      }
      
      if (!fullPoem) {
        throw new Error('No poem content received from the API');
      }

      return true;
    } catch (error) {
      console.error('Error generating poem:', error);
      setError('Failed to generate poem: ' + error.message);
      setPoem('');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [poemLanguage]);

  useEffect(() => {
    const checkAuthentication = () => {
      return !!getStoredToken();
    };

    const isUserAuthenticated = checkAuthentication();
    setIsAuthenticated(isUserAuthenticated);

    if (isUserAuthenticated) {
      const checkAndGeneratePoem = async () => {
        try {
          const existingPoem = localStorage.getItem('generatedPoem');
          const cachedPoemLanguage = normalizePoemLanguage(localStorage.getItem('poemLanguage'));
          const currentLang = poemLanguage;
          const lastGeneratedTime = localStorage.getItem('lastPoemGeneratedTime');
          const currentTime = new Date().getTime();

          if (!existingPoem || !lastGeneratedTime || 
              (currentTime - parseInt(lastGeneratedTime) > 24 * 60 * 60 * 1000) ||
              (cachedPoemLanguage !== currentLang) ||
              !poemMatchesLanguage(existingPoem, currentLang)) {
            const generated = await generatePoem();
            if (generated) {
              localStorage.setItem('lastPoemGeneratedTime', currentTime.toString());
              localStorage.setItem('poemLanguage', currentLang);
            }
          } else {
            setPoem(existingPoem);
          }
        } catch (err) {
          console.error('Error in checkAndGeneratePoem:', err);
          setError('Failed to check or generate poem');
        }
      };

      checkAndGeneratePoem();

      const interval = setInterval(() => {
        const lastGeneratedTime = localStorage.getItem('lastPoemGeneratedTime');
        const currentTime = new Date().getTime();
        const cachedPoemLanguage = normalizePoemLanguage(localStorage.getItem('poemLanguage'));
        const currentLang = poemLanguage;
        const existingPoem = localStorage.getItem('generatedPoem');
        
        if (!lastGeneratedTime || 
            (currentTime - parseInt(lastGeneratedTime) > 24 * 60 * 60 * 1000) ||
            (cachedPoemLanguage !== currentLang) ||
            !poemMatchesLanguage(existingPoem, currentLang)) {
          checkAndGeneratePoem();
        }
      }, 60 * 60 * 1000); // Check every hour

      return () => clearInterval(interval);
    } else {
      setPoem(SAMPLE_POEMS[poemLanguage] || SAMPLE_POEMS.en);
      setError('');
    }
  }, [generatePoem, poemLanguage]);

  const getPoemTitle = () => {
    return poemLanguage === 'en' ? 'Daily Scholar\'s Verse' : '文献作诗';
  };
  
  const formatPoem = (poemText, isEnglish) => {
    if (!poemText) return '';

    if (isEnglish) {
      return poemText.split('\n').map((line, i) => (
        <div key={i} className="mb-0.5">
          {line}
        </div>
      ));
    } else {
      return poemText.split('\n').map((line, i) => (
        <div key={i} className="mb-1">
          {line}
        </div>
      ));
    }
  };

  return (
    <div className={`mt-4 ${isDarkMode ? 'text-slate-100' : 'text-slate-800'}`}>
      {error ? (
        <p className="text-sm text-red-400">{error}</p>
      ) : isLoading ? (
        <div className={`flex items-center gap-2.5 py-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          <span className="inline-block h-1 w-1 animate-pulse rounded-full bg-current" />
          {poemLanguage === 'en' ? 'Crafting your poem...' : '科研喵正在作诗...'}
        </div>
      ) : (
        <div className="homepage-scrollbar max-h-64 overflow-y-auto pr-1 pb-1">
          <div className={`whitespace-pre-wrap ${
            poemLanguage === 'en'
              ? `text-center font-serif text-[0.94rem] leading-[1.9] tracking-[0.01em] ${isDarkMode ? 'text-slate-200/90' : 'text-slate-600'}`
              : `font-serif text-[1.02rem] leading-[2.1] tracking-[0.18em] ${isDarkMode ? 'text-slate-200/90' : 'text-slate-600'}`
          }`}>
            {formatPoem(poem, poemLanguage === 'en')}
          </div>
          {!isAuthenticated && (
            <p className={`mt-4 rounded-2xl border px-3 py-2.5 text-xs italic leading-relaxed ${
              isDarkMode
                ? 'border-white/[0.06] bg-white/[0.04] text-slate-400'
                : 'border-slate-200/80 bg-white/50 text-slate-500'
            }`}>
              {poemLanguage === 'en'
                ? 'Login to get personalized poems based on your papers!'
                : '登录后即可获取基于您的论文的个性化诗作！'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GeneratedPoem;
